import React, { useState } from "react";
import { toast } from "sonner";
import { Send, Phone, Mail, MapPin, Clock } from "lucide-react";
import { SectionHeading, SectionLabel } from "../components/SectionHeading";
import { useSite } from "../context/SiteContext";
import api from "../lib/api";

export default function ContactPage() {
  const { settings, services } = useSite();
  const brand = settings.brand;
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", service: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        company: form.company || null,
        service: form.service || null,
        message: form.message,
      });
      toast.success("Inquiry sent. Our team will contact you within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="border-b border-[#1F3550] relative">
        <div className="absolute inset-0 blueprint-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <SectionLabel code="X/00">Contact</SectionLabel>
          <h1 className="mt-6 font-heading font-black text-4xl md:text-6xl uppercase tracking-tight leading-[0.95] max-w-4xl">
            Send the scope.<br /><span className="text-[#F39C12]">Get a plan.</span>
          </h1>
          <p className="mt-6 text-lg text-[#94A3B8] max-w-3xl leading-relaxed">
            Tell us about your project. Our team responds within 24 hours with the
            right engineers on the call.
          </p>
        </div>
      </section>

      <section className="border-b border-[#1F3550]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <SectionHeading code="X/01" overline="Request a Quote" title="Project inquiry form." />
            <form onSubmit={onSubmit} data-testid="contact-form" className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Input testid="contact-name" label="Full Name *" name="name" value={form.name} onChange={onChange} required />
              <Input testid="contact-email" label="Email *" name="email" type="email" value={form.email} onChange={onChange} required />
              <Input testid="contact-phone" label="Phone" name="phone" value={form.phone} onChange={onChange} />
              <Input testid="contact-company" label="Company" name="company" value={form.company} onChange={onChange} />
              <div className="md:col-span-2">
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">Service of interest</label>
                <select
                  data-testid="contact-service"
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-3 outline-none"
                >
                  <option value="">-- Select Service --</option>
                  {services.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">Project details *</label>
                <textarea
                  data-testid="contact-message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  required
                  minLength={10}
                  rows={6}
                  placeholder="Describe the project scope, location, timeline..."
                  className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-3 outline-none placeholder:text-[#64748B]"
                />
              </div>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#D68910] transition-colors disabled:opacity-50"
                >
                  {submitting ? "Sending..." : "Send Inquiry"} <Send className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>

          {/* Office info */}
          <div className="lg:col-span-5">
            <div className="border border-[#1F3550] bg-[#0D1B2A] p-8">
              <SectionLabel code="X/02">Head Office</SectionLabel>
              <h3 className="mt-5 font-heading font-bold text-2xl text-[#F8FAFC]">
                {brand.name}
              </h3>
              <div className="mt-6 space-y-5">
                <InfoLine icon={MapPin} label="Address" value={brand.address} />
                <InfoLine icon={Phone} label="Phone" value={brand.phone} href={`tel:${brand.phone}`} />
                <InfoLine icon={Mail} label="Email" value={brand.email} href={`mailto:${brand.email}`} />
                <InfoLine icon={Clock} label="Working Hours" value={brand.working_hours || "Mon – Sat · 9:00 – 18:30"} />
              </div>
            </div>
            <div className="mt-6 border border-[#1F3550] bg-[#050A0F] aspect-video overflow-hidden">
              <iframe
                title="Visakhapatnam map"
                src="https://www.google.com/maps?q=Visakhapatnam,%20Andhra%20Pradesh&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(80%) invert(85%) hue-rotate(180deg)" }}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Input({ label, testid, ...props }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{label}</label>
      <input
        data-testid={testid}
        {...props}
        className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-3 outline-none placeholder:text-[#64748B]"
      />
    </div>
  );
}

function InfoLine({ icon: Icon, label, value, href }) {
  const content = (
    <div className="flex items-start gap-4">
      <Icon className="h-5 w-5 text-[#F39C12] mt-0.5 flex-shrink-0" />
      <div>
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{label}</div>
        <div className="mt-1 text-[#F8FAFC]">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:opacity-80">{content}</a> : content;
}
